import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useLang } from '../context/LangContext';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import ResourceCard from '../components/ResourceCard';
import SearchBar from '../components/SearchBar';
import {
  FaChevronLeft, FaChevronRight, FaRocket, FaStar, FaGlobe,
  FaClock, FaFire, FaSortAlphaDown, FaChevronRight as FaRight,
  FaRobot, FaServer, FaCode, FaShieldAlt, FaBullhorn,
  FaChartBar, FaChartLine, FaShareAlt, FaShoppingCart, FaMoneyBillWave,
  FaCreditCard, FaMobileAlt, FaGamepad, FaUserSecret, FaLink, FaBlog,
  FaComments, FaTools, FaStar as FaStarIcon, FaHandshake, FaDatabase,
  FaEnvelopeOpenText, FaLock, FaImage, FaNetworkWired,
  FaDesktop, FaPuzzlePiece, FaGift, FaFileAlt, FaEye, FaWifi,
  FaMapMarkerAlt, FaWordpress, FaThLarge, FaSearch as FaSearchIcon
} from 'react-icons/fa';

// Icon map for categories
const ICON_MAP = {
  FaRobot, FaServer, FaCode, FaShieldAlt, FaGlobe: FaGlobe, FaBullhorn,
  FaChartBar, FaChartLine, FaShareAlt, FaShoppingCart, FaMoneyBillWave,
  FaCreditCard, FaMobileAlt, FaGamepad, FaUserSecret, FaLink, FaBlog,
  FaComments, FaTools, FaStar: FaStarIcon, FaHandshake, FaDatabase,
  FaEnvelopeOpenText, FaLock, FaImage, FaRocket, FaNetworkWired,
  FaDesktop, FaPuzzlePiece, FaGift, FaFileAlt, FaEye, FaWifi,
  FaMapMarkerAlt, FaWordpress, FaThLarge, FaSearchIcon
};

function getIconForCategory(cat) {
  if (cat.icon && ICON_MAP[cat.icon]) return ICON_MAP[cat.icon];
  const lower = (cat.name + ' ' + cat.slug).toLowerCase();
  if (lower.includes('ai')) return FaRobot;
  if (lower.includes('seo')) return FaSearchIcon;
  if (lower.includes('server') || lower.includes('vps') || lower.includes('服务器')) return FaServer;
  if (lower.includes('wordpress')) return FaWordpress;
  if (lower.includes('spy')) return FaEye;
  if (lower.includes('blog') || lower.includes('博客')) return FaBlog;
  if (lower.includes('domain') || lower.includes('域名')) return FaGlobe;
  if (lower.includes('tool') || lower.includes('工具')) return FaTools;
  if (lower.includes('广告') || lower.includes('ad')) return FaBullhorn;
  if (lower.includes('forum') || lower.includes('论坛')) return FaComments;
  if (lower.includes('pay') || lower.includes('支付') || lower.includes('收款')) return FaCreditCard;
  if (lower.includes('traffic') || lower.includes('流量')) return FaChartBar;
  if (lower.includes('ecommerce') || lower.includes('电商')) return FaShoppingCart;
  if (lower.includes('素材') || lower.includes('material')) return FaImage;
  if (lower.includes('联盟') || lower.includes('alliance') || lower.includes('affiliate')) return FaHandshake;
  if (lower.includes('营销') || lower.includes('marketing')) return FaRocket;
  if (lower.includes('追踪') || lower.includes('track')) return FaMapMarkerAlt;
  if (lower.includes('邮件') || lower.includes('email')) return FaEnvelopeOpenText;
  if (lower.includes('mobile') || lower.includes('移动') || lower.includes('mvas')) return FaMobileAlt;
  if (lower.includes('后台') || lower.includes('backend')) return FaDatabase;
  if (lower.includes('综合') || lower.includes('compre')) return FaPuzzlePiece;
  return FaThLarge;
}

// Category section colors
const SECTION_COLORS = [
  '#6366f1', '#22d3ee', '#f472b6', '#34d399', '#fbbf24',
  '#a855f7', '#fb923c', '#60a5fa', '#f43f5e', '#14b8a6',
  '#e879f9', '#4ade80', '#f97316', '#38bdf8', '#c084fc',
];

// Compact resource card for category sections
function CompactCard({ resource }) {
  const fallbackIcon = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(resource.link)}&sz=64`;

  return (
    <Link to={`/resource/${resource.slug}`} className="compact-card">
      <div className="compact-card-icon">
        <img
          src={resource.icon_url || fallbackIcon}
          alt={resource.name}
          onError={(e) => { e.target.src = fallbackIcon; }}
        />
      </div>
      <div className="compact-card-info">
        <h4 className="compact-card-name">{resource.name}</h4>
        {resource.description && (
          <p className="compact-card-desc">{resource.description}</p>
        )}
      </div>
      <FaRight className="compact-card-arrow" />
    </Link>
  );
}

// Category section component with lazy loading
function CategorySection({ category, index, lang }) {
  const { t } = useLang();
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeSubcat, setActiveSubcat] = useState(null);
  const sectionRef = useRef(null);
  const loadedRef = useRef(false);

  const color = SECTION_COLORS[index % SECTION_COLORS.length];
  const Icon = getIconForCategory(category);

  // Re-fetch when language changes
  useEffect(() => {
    loadedRef.current = false;
  }, [lang]);

  useEffect(() => {
    const fetchData = () => {
      loadedRef.current = true;
      setLoading(true);
      const params = lang ? { lang } : {};
      api.get(`/resources/by-category/${category.id}`, { params })
        .then(res => setResources(res.data.resources))
        .catch(console.error)
        .finally(() => setLoading(false));
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadedRef.current) {
          fetchData();
        }
      },
      { rootMargin: '400px' }
    );

    // If already visible (lang changed while on screen), fetch immediately
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
      // Check if already intersecting
      const rect = sectionRef.current.getBoundingClientRect();
      if (!loadedRef.current && rect.top < window.innerHeight + 400) {
        fetchData();
      }
    }
    return () => observer.disconnect();
  }, [category.id, lang]);

  const filteredResources = resources
    ? (activeSubcat ? resources.filter(r => r.category_id === activeSubcat) : resources)
    : [];

  return (
    <section
      className="cat-section"
      id={`cat-${category.slug}`}
      ref={sectionRef}
      style={{ '--section-color': color }}
    >
      <div className="cat-section-header">
        <div className="cat-section-icon" style={{ background: `${color}18`, color }}>
          <Icon />
        </div>
        <h2 className="cat-section-title">{category.name}</h2>
        <span className="cat-section-count">{category.total_resources}</span>
      </div>

      {category.children && category.children.length > 0 && (
        <div className="cat-section-pills">
          <button
            className={`cat-pill ${activeSubcat === null ? 'active' : ''}`}
            onClick={() => setActiveSubcat(null)}
            style={activeSubcat === null ? { background: `${color}18`, color, borderColor: `${color}40` } : {}}
          >
            {t('all')}
          </button>
          {category.children.map(child => (
            <button
              key={child.id}
              className={`cat-pill ${activeSubcat === child.id ? 'active' : ''}`}
              onClick={() => setActiveSubcat(child.id)}
              style={activeSubcat === child.id ? { background: `${color}18`, color, borderColor: `${color}40` } : {}}
            >
              {child.name}
              {child.resource_count > 0 && (
                <span className="cat-pill-count">{child.resource_count}</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="cat-section-grid">
        {loading ? (
          [...Array(6)].map((_, i) => (
            <div key={i} className="compact-card-skeleton">
              <div className="skeleton-icon" />
              <div className="skeleton-lines">
                <div className="skeleton-line" style={{ width: '70%' }} />
                <div className="skeleton-line" style={{ width: '90%' }} />
              </div>
            </div>
          ))
        ) : filteredResources.length === 0 && resources !== null ? (
          <div className="cat-section-empty">{t('noResourcesInSubcat')}</div>
        ) : (
          filteredResources.map(resource => (
            <CompactCard key={resource.id} resource={resource} />
          ))
        )}
      </div>
    </section>
  );
}

function Home() {
  const [categoryTree, setCategoryTree] = useState([]);
  const [categories, setCategories] = useState([]); // flat list for sidebar
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchPagination, setSearchPagination] = useState({ total: 0, pages: 0 });
  const [searchPage, setSearchPage] = useState(1);
  const [searchSort, setSearchSort] = useState('newest');
  const [searchLoading, setSearchLoading] = useState(false);
  const [stats, setStats] = useState({ categories: 0, resources: 0 });
  const { langParam, t } = useLang();
  const [sidebarPinned, setSidebarPinned] = useState(() => localStorage.getItem('sidebar_pinned') === 'true');
  const searchRef = useRef(null);

  // Fetch category tree for main layout
  useEffect(() => {
    const params = langParam ? { lang: langParam } : {};
    api.get('/categories/tree', { params })
      .then(res => {
        setCategoryTree(res.data);
        const totalResources = res.data.reduce((sum, c) => sum + c.total_resources, 0);
        setStats({ categories: res.data.length, resources: totalResources });
      })
      .catch(console.error);
  }, [langParam]);

  // Fetch flat categories for sidebar
  useEffect(() => {
    const params = langParam ? { lang: langParam } : {};
    api.get('/categories', { params })
      .then(res => setCategories(res.data))
      .catch(console.error);
  }, [langParam]);

  // Search mode
  const fetchSearchResults = useCallback(async () => {
    if (!search) return;
    setSearchLoading(true);
    try {
      const params = { search, page: searchPage, sort: searchSort };
      if (langParam) params.lang = langParam;
      const res = await api.get('/resources', { params });
      setSearchResults(res.data.resources);
      setSearchPagination(res.data.pagination);
    } catch (err) {
      console.error(err);
    }
    setSearchLoading(false);
  }, [search, searchPage, searchSort, langParam]);

  useEffect(() => {
    if (search) fetchSearchResults();
  }, [fetchSearchResults, search]);

  const handleSearch = (query) => {
    setSearch(query);
    setSearchPage(1);
  };

  const handleCategoryClick = (slug) => {
    if (search) setSearch(''); // exit search mode
    // Scroll to category section
    setTimeout(() => {
      const el = document.getElementById(`cat-${slug}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleSearchFocus = () => {
    searchRef.current?.focus();
  };

  return (
    <div className={`app-layout ${sidebarPinned ? 'sidebar-is-pinned' : ''}`}>
      <Header />
      <Sidebar
        categories={categories}
        categoryTree={categoryTree}
        activeCategory={null}
        onCategoryClick={handleCategoryClick}
        onSearchClick={handleSearchFocus}
        onPinChange={setSidebarPinned}
      />

      <main className="main-area">
        {/* Compact Hero */}
        <section className="hero hero-compact">
          <h1 className="hero-title-sm">
            {t('heroTitle1')} <span className="hero-title-gradient">{t('heroTitle2')}</span>
          </h1>

          <SearchBar
            onSearch={handleSearch}
            totalCount={stats.resources}
            searchRef={searchRef}
          />

          <div className="hero-stats-row">
            <div className="hero-stat-pill">
              <FaGlobe />
              <strong>{stats.resources.toLocaleString()}</strong> {t('resources')}
            </div>
            <div className="hero-stat-pill">
              <FaStar />
              <strong>{stats.categories}</strong> {t('categoryCount')}
            </div>
          </div>
        </section>

        {/* Search mode: flat results */}
        {search ? (
          <div className="search-results-section">
            <div className="content-toolbar">
              <div className="toolbar-left">
                <h2 className="section-heading">{t('resultsFor')} "{search}"</h2>
              </div>
              <div className="toolbar-right">
                <button className="filter-clear-btn" onClick={() => setSearch('')}>
                  {t('clearSearch')} &times;
                </button>
                <div className="sort-toggle">
                  <button className={`sort-btn ${searchSort === 'newest' ? 'active' : ''}`}
                    onClick={() => { setSearchSort('newest'); setSearchPage(1); }}>
                    <FaClock />
                  </button>
                  <button className={`sort-btn ${searchSort === 'popular' ? 'active' : ''}`}
                    onClick={() => { setSearchSort('popular'); setSearchPage(1); }}>
                    <FaFire />
                  </button>
                  <button className={`sort-btn ${searchSort === 'name' ? 'active' : ''}`}
                    onClick={() => { setSearchSort('name'); setSearchPage(1); }}>
                    <FaSortAlphaDown />
                  </button>
                </div>
              </div>
            </div>

            {searchLoading ? (
              <div className="resource-grid">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="skeleton-card">
                    <div className="skeleton-strip" />
                    <div className="skeleton-icon-area"><div className="skeleton-icon" /></div>
                    <div className="skeleton-lines">
                      <div className="skeleton-line long" />
                      <div className="skeleton-line short" />
                    </div>
                  </div>
                ))}
              </div>
            ) : searchResults.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🔍</div>
                <h3>{t('noResults')}</h3>
                <p>{t('noResultsHint')}</p>
              </div>
            ) : (
              <>
                <div className="resource-grid">
                  {searchResults.map(resource => (
                    <ResourceCard key={resource.id} resource={resource} />
                  ))}
                </div>
                {searchPagination.pages > 1 && (
                  <div className="pagination">
                    <button disabled={searchPage <= 1} onClick={() => setSearchPage(p => p - 1)} className="page-btn">
                      <FaChevronLeft /> {t('prev')}
                    </button>
                    <div className="page-numbers">
                      {[...Array(Math.min(searchPagination.pages, 7))].map((_, i) => {
                        let pageNum;
                        if (searchPagination.pages <= 7) pageNum = i + 1;
                        else if (searchPage <= 4) pageNum = i + 1;
                        else if (searchPage >= searchPagination.pages - 3) pageNum = searchPagination.pages - 6 + i;
                        else pageNum = searchPage - 3 + i;
                        return (
                          <button key={pageNum} className={`page-num ${searchPage === pageNum ? 'active' : ''}`}
                            onClick={() => setSearchPage(pageNum)}>{pageNum}</button>
                        );
                      })}
                    </div>
                    <button disabled={searchPage >= searchPagination.pages} onClick={() => setSearchPage(p => p + 1)} className="page-btn">
                      {t('next')} <FaChevronRight />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          /* Browse mode: category sections */
          <div className="category-browse">
            {categoryTree.map((cat, i) => (
              <CategorySection key={cat.id} category={cat} index={i} lang={langParam} />
            ))}
          </div>
        )}

        {/* Footer banner area */}
        {!search && (
          <div className="home-footer-banner">
            <div className="footer-banner-content">
              <h3>{t('footerTitle')}</h3>
              <p>{t('footerDesc')}</p>
              <a href="/submit" className="footer-banner-btn">{t('footerBtn')}</a>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Home;
